import type { PropsWithChildren } from "react";
import type { SubmitHandler } from "react-hook-form";

import { useDynamicForm } from "@/features/forms/hooks";
import { filterHiddenConditionalAnswers } from "@/features/forms/utils";
import {
  useHealthieForm,
  useSubmitHealthieForm,
} from "@/features/intake-form/hooks";
import type { HealthieFormParams } from "@/features/intake-form/models";
import { LogoLoader } from "@/shared/components";
import { Form } from "@/shared/ui";
import { handleAxiosFieldErrors, tw } from "@/shared/utils";
import { HealthieFormField } from "./components";

type HealthieFormProps = PropsWithChildren & {
  onSubmit?: () => void;
  params: HealthieFormParams;
  className?: string;
};

export const HealthieForm = ({
  onSubmit,
  params,
  children,
  className,
}: HealthieFormProps) => {
  const { data: healthieForm, isLoading, isSuccess } = useHealthieForm(params);

  const form = useDynamicForm(healthieForm);

  const submitMutation = useSubmitHealthieForm();

  const handleSubmit: SubmitHandler<Record<string, string>> = (values) => {
    if (healthieForm) {
      const answers = filterHiddenConditionalAnswers(
        values,
        healthieForm.fields,
      );
      submitMutation.mutate(
        { ...params, formId: healthieForm.formId, answers },
        {
          onSuccess: onSubmit,
          onError: (err) => handleAxiosFieldErrors(err, form.setError),
        },
      );
    }
  };

  return (
    <Form.Provider {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className={tw("flex min-h-96 max-w-3xl flex-col gap-8", className)}
      >
        {isSuccess && (
          <>
            {healthieForm.fields.map((field) => (
              <HealthieFormField key={field.id} healthieField={field} />
            ))}

            {children}
          </>
        )}

        <LogoLoader
          show={isLoading || submitMutation.isPending}
          withOverlay
          fullscreen={submitMutation.isPending}
        />
      </form>
    </Form.Provider>
  );
};
